import { useState } from "react";
import { observer, inject } from "mobx-react";
import styled, { useTheme } from "styled-components";
import { format } from "date-fns";
import navigateImg from "../../assets/img/outline-keyboard_arrow_left.svg";
import { useTranslation } from "react-i18next";
import Text from "../Text";
import ToggleSwitch from "../ToggleSwitch";
import SegmentedToggle from "../SegmentedToggle";
import { buildModeSegments } from "../../utils/spaceModes";
import { useMobile } from "../../utils/mobile";
import { ClipLoader } from "react-spinners";

const StyledBox = styled.div(
  ({ theme, noBorder, isRowClickable, isMobile }) => ({
    flexWrap: isMobile ? "wrap" : "nowrap",
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    borderBottom: !!noBorder
      ? `1px solid transparent`
      : `1px solid ${theme.color.gray50}`,
    width: "100%",
    cursor: isRowClickable === true ? "pointer" : "default",
    gap: 4,
    // Hover lifts the row off the list: soft shadow and no divider, as in the
    // design. Skipped on touch devices, where there is no hover state.
    ...(isMobile
      ? {}
      : {
        transition: "box-shadow 0.15s ease",
        "&:hover": {
          boxShadow: `0 2px 12px ${theme.color.gray50}`,
          borderBottomColor: "transparent",
        },
      }),
  })
);

const BoxContent = styled.div(({ theme, noBorder, width, margin, isRtl }) => ({
  width: width,
  margin: margin ? margin : "13px 0",

  [`&:nth-child(even)`]: {
    // textAlign: "end",
  },
  // [`&:not(:last-child)`]: {
  //   borderLeft: !!noBorder
  //     ? `none`
  //     : isRtl
  //     ? `1px solid ${theme.color.gray50}`
  //     : `none`,
  //   borderRight: !!noBorder
  //     ? `none`
  //     : !isRtl
  //     ? `1px solid ${theme.color.gray50}`
  //     : `none`,
  // },
}));

// A missing value shows a dash, not 0 — while a real 0 still shows as 0.
const formatCellValue = (value) =>
  value === undefined || value === "" ? "-" : value.toLocaleString();

const TabelRow = ({
  keys,
  row,
  translation,
  bold,
  noBorder,
  margin,
  toDetails = () => { },
  onRowClick = () => { },
  onToggle,
  onFlagToggle,
  flagGate,
  isRowClickable = false,
  currency,
  valueWidth,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation(translation);
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useMobile();

  const showDetails = async (row) => {
    setIsLoading(true);
    try {
      await toDetails(row);
    } catch (error) {
      console.error("Error navigating to details:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleToggle = async (event, row, toggle) => {
    const success = await onToggle(row, toggle);
    return success;
  };

  return (
    <StyledBox
      noBorder={noBorder}
      isMobile={isMobile}
      onClick={() => onRowClick(row)}
      isRowClickable={isRowClickable}
    >
      {!!row && (
        <>
          {keys.map((key, index) => {
            return (
              <BoxContent
                margin={margin}
                width={valueWidth || key.width}
                key={`${key}${index}`}
                noBorder={noBorder}
                isRtl={i18n.dir() === "rtl"}
              >
                {key.key.includes("MODES") ? (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <SegmentedToggle
                      segments={buildModeSegments(row, t, !!onFlagToggle, flagGate)}
                      onToggle={(flagKey, next) =>
                        onFlagToggle(row, flagKey, next)
                      }
                    />
                  </div>
                ) : key.key.includes("ACTIVE") ? (
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <ToggleSwitch
                      disabled={!onToggle}
                      toggled={row[key.key] === "Y"}
                      onToggle={(event, toggle) =>
                        handleToggle(event, row, toggle)
                      }
                    />
                  </div>
                ) : key.key.includes("TODETAILS") ? (
                  isLoading ? (
                    <div
                      style={{
                        marginInlineStart: 20,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <ClipLoader color={"#000000"} size={20} />
                    </div>
                  ) : (
                    <img
                      src={navigateImg}
                      alt="navigate"
                      style={{ cursor: "pointer", marginInlineStart: 20 }}
                      onClick={() => showDetails(row)}
                    />
                  )
                ) : bold ? (
                  <Text.BB
                    style={{
                      color: theme.color.navy,
                      textAlign: "start",
                      cursor: "inherit",
                    }}
                  >
                    {`${row[key.key] === null
                      ? "-"
                      : key.key.includes("DATE")
                        ? format(new Date(row[key.key]), "dd/MM/yyyy")
                        : formatCellValue(row[key.key])
                      }  ${key.currency && currency ? currency : ``}`}
                  </Text.BB>
                ) : (
                  <Text.SM
                    style={{
                      color: theme.color.gray90,
                      textAlign: "start",
                      paddingInlineStart: 14,
                      cursor: "inherit",
                    }}
                  >
                    {`${row[key.key] === null
                      ? "-"
                      : key.key.includes("INDEXCURRENCYNAME")
                        ? !row["INDEXCURRENCYNAME"] ||
                          row["NOINDEXATION"] === "Y"
                          ? t("accounts:no_index")
                          : `${t("accounts:indexed_to")}${row["INDEXCURRENCYNAME"]
                          } ${row["NOINTEREST"] === "Y"
                            ? t("accounts:without_interst")
                            : t("accounts:with_interst")
                          }`
                        : key.key.includes("DATE")
                          ? format(new Date(row[key.key]), "dd/MM/yyyy")
                          : formatCellValue(row[key.key])
                      }  ${key.currency && currency ? currency : ``}`}
                  </Text.SM>
                )}
              </BoxContent>
            );
          })}
        </>
      )}
    </StyledBox>
  );
};

export default inject("tenantsStore")(observer(TabelRow));
